import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTrash, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Button, Table, Pagination, Badge, Spinner, Alert, Modal } from '@themesberg/react-bootstrap';
import api from '../../utils/api';
import { formatPurchaseDateTime } from '../../utils/dateTime';

export default () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, activeFilter, searchTerm, startDate, endDate]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/billing/purchases?page=1&limit=5000&type=credit');
      const payload = response.data || {};
      const paymentsList = Array.isArray(payload)
        ? payload
        : (payload.purchases || payload.data || payload.payments || []);
      
      const creditPayments = paymentsList.filter((p) => {
        const type = String(p.type || '').toLowerCase();
        const sku = String(p.sku || p.productId || '').toLowerCase();
        if (type === 'vip' || type === 'verified_badge') return false;
        if (sku.includes('vip')) return false;
        return true;
      }).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setPayments(creditPayments);
    } catch (err) {
      console.error("Error loading credit package payments:", err);
      setError("Ödemeler yüklenirken hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    try {
      setDeleting(true);
      const amount = parseFloat(selectedPayment.price || 0);
      await api.delete(`/billing/purchases/${selectedPayment.id || selectedPayment._id}`);
      
      if (amount > 0 && selectedPayment.status !== "Sahte Alım") {
        try {
            await api.put('/settings/dashboardStats/decrement', { amount });
        } catch (e) {
            console.error("Could not update dashboard stats", e);
        }
      }

      setPayments(payments.filter(p => (p.id || p._id) !== (selectedPayment.id || selectedPayment._id)));
      setShowDeleteModal(false);
      setSelectedPayment(null);
    } catch (err) {
      console.error("Error deleting credit payment:", err);
      setError("Silme işlemi sırasında hata oluştu: " + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setDeletingAll(true);
      setDeleteProgress("Kayıtlar yükleniyor...");
      setDeleteProgress("Toplu silme başlatılıyor...");
      await api.delete('/billing/purchases-all/truncate-credit'); // Assuming there is an endpoint for this, or we just rely on generic truncate
      setPayments([]);
      setDeleteProgress("");
      setShowDeleteAllModal(false);
    } catch (err) {
      console.error("Error deleting all credit payments:", err);
      setError("Toplu silme sırasında hata oluştu: " + err.message);
    } finally {
      setDeletingAll(false);
    }
  };

  const filterPayments = () => {
    let filtered = [...payments];

    // Tarih filtresi
    const now = new Date();
    switch (activeFilter) {
      case "day":
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => {
          const paymentDate = new Date(p.createdAt || p.date || 0);
          return paymentDate >= oneDayAgo;
        });
        break;
      case "week":
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => {
          const paymentDate = new Date(p.createdAt || p.date || 0);
          return paymentDate >= oneWeekAgo;
        });
        break;
      case "year":
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => {
          const paymentDate = new Date(p.createdAt || p.date || 0);
          return paymentDate >= oneMonthAgo;
        });
        break;
      case "custom":
        if (startDate || endDate) {
          filtered = filtered.filter(p => {
            const paymentDate = new Date(p.createdAt || p.date || 0);
            const isAfterStart = startDate ? paymentDate >= new Date(startDate) : true;
            const isBeforeEnd = endDate ? paymentDate <= new Date(endDate) : true;
            return isAfterStart && isBeforeEnd;
          });
        }
        break;
      default:
        break;
    }

    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        (p.userName && p.userName.toLowerCase().includes(searchLower)) ||
        (p.userEmail && p.userEmail.toLowerCase().includes(searchLower)) ||
        (p.sku && p.sku.toLowerCase().includes(searchLower))
      );
    }

    setFilteredPayments(filtered);
    setCurrentPage(1);
  };

  // Özet hesaplamaları
  const calculateTotals = () => {
    const filtered = filteredPayments.filter(p => p.status !== "Sahte Alım");
    const googlePayTotal = filtered
      .filter(p => p.paymentMethod === "Google Pay" || p.paymentMethod === "Google Play" || (!p.paymentMethod && !p.orderId?.startsWith("ERO")))
      .reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
    // PayTR ödemelerini de kredi kartı olarak say
    const creditCardTotal = filtered
      .filter(p => p.paymentMethod === "KK İle Ödeme" || p.paymentMethod === "Kredi Kartı" || p.paymentMethod?.includes("PayTR") || p.orderId?.startsWith("ERO"))
      .reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
    const generalTotal = filtered.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);

    return {
      googlePay: googlePayTotal.toFixed(2),
      creditCard: creditCardTotal.toFixed(2),
      general: generalTotal.toFixed(2)
    };
  };

  const totals = calculateTotals();

  // Sayfalama
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  const formatDate = (date) => formatPurchaseDateTime(date);

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Buttons */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        <Button
          variant={activeFilter === "all" ? "info" : "outline-dark"}
          onClick={() => setActiveFilter("all")}
        >
          Tümü
        </Button>
        <Button
          variant={activeFilter === "day" ? "info" : "outline-dark"}
          onClick={() => setActiveFilter("day")}
        >
          Son 1 Gün
        </Button>
        <Button
          variant={activeFilter === "week" ? "info" : "outline-dark"}
          onClick={() => setActiveFilter("week")}
        >
          Son 1 Hafta
        </Button>
        <Button
          variant={activeFilter === "year" ? "info" : "outline-dark"}
          onClick={() => setActiveFilter("year")}
        >
          Son 1 Ay
        </Button>
        <Button
          variant={activeFilter === "custom" ? "info" : "outline-dark"}
          onClick={() => setActiveFilter("custom")}
        >
          Özel Aralık
        </Button>
        {activeFilter === "custom" && (
          <div className="d-flex gap-2 align-items-center flex-wrap mt-2 mt-md-0">
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              style={{ width: 'auto' }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span className="text-white">-</span>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              style={{ width: 'auto' }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        )}
        <Button
          variant="dark"
          onClick={() => setActiveFilter("balance")}
        >
          Başlangıç Saati
        </Button>
        <Button
          variant="dark"
          onClick={() => setActiveFilter("end")}
        >
          Bitiş Saati
        </Button>
        <Button
          variant="danger"
          className="ms-auto"
          onClick={() => setShowDeleteAllModal(true)}
          disabled={payments.length === 0}
        >
          <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
          Tümünü Sil ({payments.length})
        </Button>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm" style={{ backgroundColor: '#d4edda' }}>
            <Card.Body>
              <div className="border-start border-success border-4 ps-3">
                <h6 className="text-muted mb-1">Google Pay Toplamı</h6>
                <p className="text-muted small mb-1">Toplam Google Pay Ödemeleri</p>
                <h3 className="text-success mb-0">
                  <FontAwesomeIcon icon={faPlay} className="me-2" />
                  {totals.googlePay}₺
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm" style={{ backgroundColor: '#d1ecf1' }}>
            <Card.Body>
              <div className="border-start border-info border-4 ps-3">
                <h6 className="text-muted mb-1">Kredi Kartı Toplamı</h6>
                <p className="text-muted small mb-1">Toplam Kredi Kartı Ödemeleri</p>
                <h3 className="text-info mb-0">
                  💳 {totals.creditCard}₺
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
            <Card.Body>
              <div className="border-start border-dark border-4 ps-3">
                <h6 className="text-muted mb-1">Genel Toplam</h6>
                <p className="text-muted small mb-1">Tüm Ödemelerin Toplamı</p>
                <h3 className="text-dark mb-0">
                  💰 {totals.general}₺
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Ara... (İsim, Email, SKU)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '100%' }}
        />
      </div>

      {/* Payments Table */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center p-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table className="table-dark table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Kullanıcı</th>
                      <th>Email</th>
                      <th>Ücret</th>
                      <th>Önceki Kredi</th>
                      <th>Şimdiki Kredi</th>
                      <th>SKU</th>
                      <th>Ödeme Yöntemi</th>
                      <th>Eklenen Kredi</th>
                      <th>Durum</th>
                      <th>Tarih</th>
                      <th style={{ width: '50px' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPayments.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center p-4">
                          {searchTerm ? "Arama sonucu bulunamadı." : "Henüz kredi paket ödemesi bulunmuyor."}
                        </td>
                      </tr>
                    ) : (
                      paginatedPayments.map((payment, index) => (
                        <tr key={payment.id}>
                          <td className="text-center">{startIndex + index + 1}</td>
                          <td>{payment.userName || payment.user || "Kullanıcı"}</td>
                          <td>{payment.userEmail || payment.email || ""}</td>
                          <td>{parseFloat(payment.price || 0).toFixed(2)}₺</td>
                          <td>{payment.previousCredits || payment.oldCredit || 0}</td>
                          <td>{payment.newCredits || payment.currentCredit || 0}</td>
                          <td>{payment.sku}</td>
                          <td>
                            <span className="text-success">
                              <FontAwesomeIcon icon={faPlay} className="me-1" />
                              {payment.paymentMethod || payment.payment || "Google Pay"}
                            </span>
                          </td>
                          <td>{payment.coinAmount || payment.addedCredit || 0}</td>
                          <td>
                            <Badge bg={payment.status === "Sahte Alım" ? "danger" : "success"} className="badge-sm">
                              {payment.status || "Başarılı Ödeme"}
                            </Badge>
                          </td>
                          <td>{formatDate(payment.createdAt || payment.date)}</td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="p-1"
                              onClick={() => {
                                setSelectedPayment(payment);
                                setShowDeleteModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center p-3 bg-dark text-white">
                <small>Toplam {filteredPayments.length} kayıt</small>
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </select>
                  <Pagination className="mb-0" size="sm">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={currentPage === pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Silme Onay Modalı */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ödeme Kaydını Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu ödeme kaydını silmek istediğinize emin misiniz? Bu işlem sadece gelir bilgisini düzeltmek içindir, kullanıcının jetonlarına müdahale etmez.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : "Sil"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tümünü Sil Onay Modalı */}
      <Modal show={showDeleteAllModal} onHide={() => !deletingAll && setShowDeleteAllModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">⚠️ Tüm Kredi Ödemelerini Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Toplam {payments.length} kredi ödeme kaydı silinecek.</strong></p>
          <p className="text-muted">Bu işlem geri alınamaz! Tüm kredi satın alma geçmişi kalıcı olarak silinecektir. Kullanıcıların mevcut jetonlarına müdahale etmez.</p>
          {deleteProgress && <Alert variant="info">{deleteProgress}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteAllModal(false)} disabled={deletingAll}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={deletingAll}>
            {deletingAll ? <><Spinner animation="border" size="sm" className="me-1" />{deleteProgress || 'Siliniyor...'}</> : `Evet, ${payments.length} Kaydı Sil`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
